import type { MessageDataObject, MessagePlaceholderDataObject } from '#models/services';
import { useMutation } from '#react-query';
import { createChatroomMessage } from '../../../services';
import { useAddChatroomMessageToCache, useDeleteChatroomMessageInCache } from '../../cache';
import { useCreatePlaceholderMessage, useReplaceChatroomMessageInCache } from './hooks';

export type CreateMessagePayload = {
  meta?: CreateMessagePayloadMeta;
  ref: string;
  type: 'media';
};

type CreateMessage = {
  chatroomId: string;
  data: CreateMessageData;
};

type CreateMessageData = {
  _id?: string;
  payload?: CreateMessagePayload[];
  text?: string;
};

type CreateMessageMutationContext = {
  message: MessagePlaceholderDataObject;
};

type CreateMessagePayloadMeta = {
  alts?: string[];
  height?: number;
  mimeType: string;
  name?: string;
  width?: number;
};

export const useCreateMessageMutation = () => {
  const addChatroomMessageToCache = useAddChatroomMessageToCache();
  const createPlaceholderMessage = useCreatePlaceholderMessage();
  const deleteChatroomMessageInCache = useDeleteChatroomMessageInCache();
  const replaceChatroomMessageInCache = useReplaceChatroomMessageInCache();

  return useMutation<MessageDataObject, Error, CreateMessage, CreateMessageMutationContext>({
    async mutationFn({ chatroomId, data: { payload, text } }) {
      const { data: messageData } = await createChatroomMessage({
        body: {
          payload,
          text,
        },
        routeParams: { chatroomId },
      });

      return messageData;
    },
    onMutate({ chatroomId, data }) {
      const messageId = data._id ?? crypto.randomUUID();
      const message = createPlaceholderMessage({
        chatroomId,
        messageId,
        payload: data.payload,
        status: 'sending',
        text: data.text,
      });

      if (data._id) {
        deleteChatroomMessageInCache({ chatroomId, messageId: data._id });
      }

      addChatroomMessageToCache({ chatroomId, message });

      return { message };
    },
    // eslint-disable-next-line perfectionist/sort-objects -- @tanstack/query/mutation-property-order requires onError before onSuccess
    onError(_error, _, context) {
      if (!context) {
        return;
      }

      replaceChatroomMessageInCache({
        message: { ...context.message, status: 'failed' as const },
        messageIdToReplace: context.message._id,
      });
    },
    onSuccess(newMessage, _, { message }) {
      replaceChatroomMessageInCache({ message: newMessage, messageIdToReplace: message._id });
    },
  });
};
